// Il catalogo degli automatismi (le «lane» dello scheduler) e delle
// connessioni esterne, scritto per chi decide se accendere qualcosa.
//
// Ridisegno 28/10/2026 (Davide: «Impostazioni e Istruzioni devono avere tutte
// le cose necessarie»). Prima il nome leggibile c'era solo per meta' delle
// lane e la spiegazione solo per quelle che comprano: le altre si leggevano
// come slug tecnici. Qui ogni lane ha nome, area e una riga su cosa fa.
//
// Lo stato di una connessione NON e' una sonda nuova: viene dall'ultimo giro
// degli automatismi che la usano (GET /api/sync/last-runs). Un giro riuscito
// alle 15:29 prova che alle 15:29 il servizio rispondeva; e' la prova che
// abbiamo gia', senza chiamare nessuno all'apertura della pagina.

export type AreaId =
  | "ordini"
  | "fornitori"
  | "spedizioni"
  | "fatture"
  | "clienti"
  | "numeri"
  | "manutenzione";

export const AREE: { id: AreaId; nome: string }[] = [
  { id: "ordini", nome: "Ordini in entrata" },
  { id: "fornitori", nome: "Fornitori e magazzino" },
  { id: "spedizioni", nome: "Spedizioni e tracking" },
  { id: "fatture", nome: "Fatture" },
  { id: "clienti", nome: "Clienti e messaggi" },
  { id: "numeri", nome: "Numeri e report" },
  { id: "manutenzione", nome: "Manutenzione" },
];

export type LaneInfo = {
  nome: string;
  area: AreaId;
  /** Una riga: cosa fa, detto semplice. */
  cosa: string;
  /** Presente solo se la lane agisce FUORI da OM (compra, scrive al cliente,
   *  crea documenti fiscali): e' il testo della conferma prima di accenderla
   *  o eseguirla. */
  effetto?: string;
};

export const LANE: Record<string, LaneInfo> = {
  // Ordini in entrata
  orders: {
    nome: "Ordini da Poleepo",
    area: "ordini",
    cosa: "Porta in OM gli ordini Amazon, che entrano ancora da Poleepo.",
  },
  mirakl: {
    nome: "Leroy Merlin, Maisons du Monde, Vente-Unique",
    area: "ordini",
    cosa: "Legge gli ordini direttamente da Mirakl, con rimborsi e stati delle righe.",
  },
  metro: {
    nome: "Metro",
    area: "ordini",
    cosa: "Legge gli ordini Metro Italia e Spagna.",
  },
  bricobravo: {
    nome: "Brico Bravo",
    area: "ordini",
    cosa: "Legge gli ordini Brico Bravo.",
  },
  shopify: {
    nome: "Kery",
    area: "ordini",
    cosa: "Legge gli ordini del negozio Kery (Shopify).",
  },
  temu: {
    nome: "Temu",
    area: "ordini",
    cosa: "Legge gli ordini Temu con indirizzo e prodotti.",
  },
  ship_deadline: {
    nome: "Scadenze di spedizione",
    area: "ordini",
    cosa: "Legge da ogni canale entro quando va spedito ogni ordine.",
  },

  // Fornitori e magazzino
  as_invio: {
    nome: "AllService · invio ordini",
    area: "fornitori",
    cosa: "Manda ad AllService la mail con gli ordini nuovi.",
    effetto:
      "Manda la mail d'ordine ad ALL SERVICE per gli ordini idonei non ancora inviati (fino a 50 per giro). Quello che parte e' un ordine vero al fornitore.",
  },
  as_tracking: {
    nome: "AllService · tracking",
    area: "fornitori",
    cosa: "Scarica il file tracking di AllService e lo scrive sugli ordini.",
  },
  cosma_invio: {
    nome: "Cosma · invio ordini",
    area: "fornitori",
    cosa: "Carica sull'FTP Cosma gli ordini con margine sopra il 15%.",
    effetto:
      "Carica sull'FTP Cosma gli ordini Cosma degli ultimi giorni con margine sopra la soglia (15%) e peso e misure del collo nel PIM. Quello che parte e' un ordine vero al fornitore.",
  },
  bizzotto_invio: {
    nome: "Bizzotto · ordini automatici",
    area: "fornitori",
    cosa: "Ordina da solo sul portale Bizzotto gli ordini con margine sopra il 15%.",
    effetto:
      "Ordina da solo su my.bizzotto.com gli ordini Bizzotto degli ultimi 7 giorni con margine sopra il 15%, solo se tutte le righe sono Bizzotto e il portale ha la merce. Chi resta fuori lo dice la campanella. Quello che parte e' un ordine vero al fornitore.",
  },
  itamoby_invio: {
    nome: "Itamoby · ordini automatici",
    area: "fornitori",
    cosa: "Ordina da solo sul portale Itamoby gli ordini con margine sopra il 15%.",
    effetto:
      "Ordina da solo sul portale Itamoby gli ordini Itamoby degli ultimi 7 giorni con margine sopra il 15%, solo se tutte le righe sono Itamoby e il portale non da' avvisi (merce esaurita, bancale). Chi resta fuori lo dice la campanella. Quello che parte e' un ordine vero al fornitore, da pagare con bonifico.",
  },
  ityhome_invio: {
    nome: "Ityhome · ordini automatici",
    area: "fornitori",
    cosa: "Ordina da solo sul portale Ityhome gli ordini con margine sopra il 15%.",
    effetto:
      "Ordina da solo sul portale Ityhome gli ordini Ityhome degli ultimi 7 giorni con margine sopra il 15%, solo se tutte le righe sono Ityhome e il portale non da' avvisi (merce esaurita, bancale). Chi resta fuori lo dice la campanella. Quello che parte e' un ordine vero al fornitore, da pagare con bonifico.",
  },
  wf_invio: {
    nome: "Web Furniture · invio ordini",
    area: "fornitori",
    cosa: "Manda a Web Furniture la mail d'ordine con la LDV allegata.",
    effetto:
      "Manda la mail d'ordine a Web Furniture (LDV allegata) per gli ordini WF degli ultimi 7 giorni con margine sopra il 15%, solo se tutte le righe sono Web Furniture e hanno peso e misure dei colli nel PIM. Chi resta fuori lo dice la campanella. Quello che parte e' un ordine vero al fornitore.",
  },
  ldv_ritiro_auto: {
    nome: "Itamoby e Ityhome · LDV al fornitore",
    area: "fornitori",
    cosa: "Manda la LDV quando la merce e' pronta e i colli tornano.",
    effetto:
      "Preme da solo «Invia LDV» per Itamoby e Ityhome (merce pronta degli ultimi 2 giorni, LDV non ancora inviata): stessa mail del tasto, ma solo se i colli scritti dal fornitore nella merce pronta sono uguali a quelli della LDV. Se non tornano, o oltre 15 colli SDA, non manda e avvisa. La mail al fornitore non si annulla.",
  },
  suppliers: {
    nome: "Tracking dai portali fornitore",
    area: "fornitori",
    cosa: "Legge il tracking dai siti dei fornitori.",
  },
  supplier_mail: {
    nome: "Posta fornitori",
    area: "fornitori",
    cosa: "Legge conferme, merce pronta e fatture dei fornitori. Non segna niente come letto.",
  },
  warehouse_stock: {
    nome: "Magazzino BS COM",
    area: "fornitori",
    cosa: "Legge le giacenze da MATECPRO: la merce in casa ferma gli ordini ai fornitori.",
  },

  // Spedizioni e tracking
  spedizione_scadenza: {
    nome: "Spedito alla scadenza",
    area: "spedizioni",
    cosa: "Alle 18 e alle 23 segna spediti gli ordini in scadenza; ogni 15 minuti quelli gia' scansionati.",
    effetto:
      "Alle 18 e alle 23 segna spediti sul marketplace gli ordini che scadono oggi o sono scaduti da al massimo 7 giorni: genera la LDV se manca, conferma senza tracking gli AllService dove il canale lo accetta e, su Brico Bravo, col tracking di un altro ordine. A ogni giro (15 minuti) segna spediti anche gli ordini col pacco gia' scansionato dal corriere, senza aspettare la scadenza. Il cliente riceve la mail di spedizione e non si annulla. Finche' OM_SPEDIZIONE_SCADENZA_PROVA e' attiva non scrive nulla: la notifica dice cosa avrebbe fatto.",
  },
  as_spedizione: {
    nome: "AllService · spedito al tracking",
    area: "spedizioni",
    cosa: "Segna spedito l'ordine appena il tracking arriva nel file AllService.",
    effetto:
      "Segna spediti sul marketplace gli ordini ALL SERVICE appena arriva il loro tracking dal file AllService, Amazon compreso (un feed per ordine, mai un secondo). Il cliente riceve la mail di spedizione, e non si annulla.",
  },
  amazon_feeds: {
    nome: "Amazon · esito dello spedito",
    area: "spedizioni",
    cosa: "Controlla se Amazon ha accettato ogni conferma di spedizione.",
  },
  tracking_events: {
    nome: "Tracking corrieri",
    area: "spedizioni",
    cosa: "Legge dove sono i pacchi: Poste e SDA, BRT. Porta al canale le LDV rifatte.",
  },
  kery_evasione: {
    nome: "Kery · consegnato",
    area: "spedizioni",
    cosa: "Segna «Consegnato» su Shopify quando lo dice il corriere.",
    effetto:
      "Scrive «Consegnato» sugli ordini Kery che il corriere ha consegnato. Da li' parte la mail di recensione di Judge.me al cliente, e non si annulla.",
  },
  pickups_auto: {
    nome: "Ritiri SDA automatici",
    area: "spedizioni",
    cosa: "Prenota il corriere per i magazzini con l'automatico acceso in Ritiri.",
    effetto:
      "Prenota il ritiro SDA per i magazzini con l'automatico acceso nella pagina Ritiri, all'orario scelto, se ci sono ordini pronti con LDV. Il corriere passa davvero, e da OM il ritiro non si annulla.",
  },
  reclami_bracchi: {
    nome: "Reclami corriere",
    area: "spedizioni",
    cosa: "Ogni ora legge le mail con Bracchi e aggiorna le pratiche di rimborso. Solo lettura.",
  },

  // Fatture
  invoicing: {
    nome: "Proposte fattura",
    area: "fatture",
    cosa: "Prepara proposte fattura e note di credito dagli ordini. Non emette niente.",
  },
  fatture_auto: {
    nome: "Emissione automatica fatture",
    area: "fatture",
    cosa: "Alle 18 emette su Eureka le fatture pronte (al massimo 30). Alle 16 avvisa su Slack.",
    effetto:
      "Alle 18 emette su Eureka le fatture pronte, una alla volta e in ordine di data, al massimo 30: oltre non emette niente e avvisa. Sono documenti fiscali veri e Eureka non li cancella: uno sbagliato si corregge solo con uno storno.",
  },
  fatture_estere: {
    nome: "Fatture estere",
    area: "fatture",
    cosa: "Legge le fatture dei fornitori esteri dalla posta e da Mirakl. Metro alle 1, 7, 13 e 19.",
  },
  saldi_marketplace: {
    nome: "Saldi marketplace",
    area: "fatture",
    cosa: "Ogni ora legge il saldo sul conto di ogni marketplace, Shopify Payments e PayPal. Solo lettura.",
  },

  // Clienti e messaggi
  mail: {
    nome: "Messaggi clienti",
    area: "clienti",
    cosa: "Scarica i messaggi dei clienti da marketplace e posta.",
  },
  whatsapp: {
    nome: "WhatsApp · messaggi",
    area: "clienti",
    cosa: "Legge i messaggi WhatsApp dei clienti Kery.",
  },
  whatsapp_avvisi: {
    nome: "WhatsApp · avvisi ai clienti Kery",
    area: "clienti",
    cosa: "Manda «ordine ricevuto» e «ordine partito» a chi l'ha chiesto.",
    effetto:
      "Manda su WhatsApp «ordine ricevuto» e «ordine partito» ai clienti Kery che hanno dato il consenso. Un messaggio mandato non si annulla.",
  },
  review_request: {
    nome: "Amazon · richiesta recensione",
    area: "clienti",
    cosa: "Chiede la recensione ai clienti Amazon contenti, tra 5 e 30 giorni dalla consegna.",
    effetto:
      "Amazon manda una mail vera ai clienti scelti per chiedere la recensione. Una sola per ordine, e non si annulla.",
  },
  mdm_recensione: {
    nome: "Maisons du Monde · richiesta valutazione",
    area: "clienti",
    cosa: "Scrive ai clienti Maisons du Monde per chiedere una valutazione.",
    effetto:
      "Apre una conversazione sull'ordine Maisons du Monde e chiede al cliente una valutazione. Una sola per ordine, e un messaggio mandato non si annulla.",
  },

  // Numeri e report
  order_margins: {
    nome: "Margini ordine",
    area: "numeri",
    cosa: "Ricalcola il margine di ogni ordine per la BI.",
  },
  cosma_costi: {
    nome: "Cosma — costi veri",
    area: "numeri",
    cosa: "Legge dal portale B2B Cosma il costo vero degli ordini dove manca. Solo lettura.",
  },
  fatture_corriere: {
    nome: "Fatture corriere — spedizione vera",
    area: "numeri",
    cosa: "Legge dalla posta le fatture Bracchi e BRT e mette il costo vero di ogni spedizione nel margine.",
  },
  note_credito_fornitori: {
    nome: "Note di credito Itamoby/Ityhome",
    area: "numeri",
    cosa: "Legge dalla posta le note di credito dei resi e i pezzi dei ricambi, e li collega all'ordine: rimborso vero e pezzi rotti nella BI.",
  },
  motivi_mirakl: {
    nome: "Motivi dei messaggi Mirakl",
    area: "numeri",
    cosa: "Legge da Leroy Merlin, Maisons du Monde e Vente-Unique il nome di ogni motivo dei messaggi, per i problemi tipici nella BI. Solo lettura.",
  },
  economics_params: {
    nome: "Parametri di margine",
    area: "numeri",
    cosa: "Copia commissioni e costi da Analisi Marketplace.",
  },
  slack_report: {
    nome: "Report su Slack",
    area: "numeri",
    cosa: "Alle 10 manda su Slack il report del giorno.",
  },
  tempi_consegna: {
    nome: "Tempi di consegna",
    area: "numeri",
    cosa: "Di notte ricalcola i giorni di consegna di ogni prodotto, per il PIM.",
  },

  // Manutenzione
  assistant: {
    nome: "Controllo di salute",
    area: "manutenzione",
    cosa: "Una volta al giorno prova le connessioni e accende la campanella se qualcosa non va.",
  },
  retention: {
    nome: "Pulizia dati personali",
    area: "manutenzione",
    cosa: "Toglie i dati personali dagli ordini chiusi da tempo (regola Amazon). Senza permesso sul server conta e basta.",
  },
  sync_pulizia: {
    nome: "Pulizia storico lavori",
    area: "manutenzione",
    cosa: "Toglie lo storico vecchio dei lavori automatici.",
  },
};

/** Nome leggibile; una lane senza voce resta col suo slug, ed e' voluto: e'
 *  una lane nuova (o orfana) e va letta come tale, non spacciata per altro. */
export function nomeLane(module: string): string {
  return LANE[module]?.nome ?? module;
}

export function areaLane(module: string): AreaId {
  return LANE[module]?.area ?? "manutenzione";
}

/** Quello che arriva da GET /api/sync/last-runs, per modulo. */
export type LastRun = {
  module: string;
  interval_seconds: number;
  enabled: boolean;
  last_started_at: string | null;
  last_finished_at: string | null;
  last_status: string | null;
  last_error: string | null;
  running: boolean;
  running_started_at: string | null;
  next_due_at: string | null;
};

export type HaltedLane = { lane: string; reason: string; halted_at: string };

export type StatoLane =
  | "fermata" // il supervisor l'ha fermata: non riparte fino al riavvio
  | "errore"
  | "ritardo" // accesa ma l'ultimo giro e' troppo vecchio
  | "in_corso"
  | "ok"
  | "spenta"
  | "mai";

export type Lettura = {
  stato: StatoLane;
  /** Quando e' successo quello che lo stato racconta (ISO). */
  quando: string | null;
  /** Il motivo, se c'e' (errore o fermo). */
  motivo: string | null;
};

const ERRORE = new Set(["failed", "dead_letter"]);

/** Lo stato di una lane, dal piu' grave. ``adesso`` e' iniettabile per i test. */
export function leggiLane(
  module: string,
  enabled: boolean,
  intervalSeconds: number,
  run: LastRun | undefined,
  halted: HaltedLane[],
  adesso: number = Date.now(),
): Lettura {
  // Il supervisor registra i fermi col nome del suo ciclo (lifespan.py):
  // «sync worker[orders]» ferma solo quella lane. «sync worker[other]» e
  // «sync scheduler» fermano tutto il resto: li mostra la pagina in cima,
  // non qui riga per riga.
  const fermo = halted.find((h) => h.lane === `sync worker[${module}]`);
  if (fermo) return { stato: "fermata", quando: fermo.halted_at, motivo: fermo.reason };
  if (run?.running) return { stato: "in_corso", quando: run.running_started_at, motivo: null };
  if (!enabled) {
    return { stato: "spenta", quando: run?.last_finished_at ?? null, motivo: null };
  }
  if (!run || !run.last_status) return { stato: "mai", quando: null, motivo: null };
  if (ERRORE.has(run.last_status)) {
    return { stato: "errore", quando: run.last_finished_at, motivo: run.last_error };
  }
  // Tre intervalli senza un giro concluso, con un minimo di un'ora: sotto,
  // un worker carico o un riavvio darebbero falsi allarmi ogni 5 minuti.
  const fine = run.last_finished_at ? Date.parse(run.last_finished_at) : NaN;
  const soglia = Math.max(intervalSeconds * 3, 3600) * 1000;
  if (Number.isFinite(fine) && adesso - fine > soglia) {
    return { stato: "ritardo", quando: run.last_finished_at, motivo: null };
  }
  return { stato: "ok", quando: run.last_finished_at, motivo: null };
}

/** Gli stati che chiedono attenzione: finiscono in cima alla pagina. */
export const STATI_PROBLEMA: ReadonlySet<StatoLane> = new Set(["fermata", "errore", "ritardo"]);

export type GruppoConnessione = "canali" | "fornitori" | "corrieri" | "posta" | "conti";

export const GRUPPI_CONNESSIONE: { id: GruppoConnessione; nome: string }[] = [
  { id: "canali", nome: "Canali di vendita" },
  { id: "fornitori", nome: "Fornitori e magazzino" },
  { id: "corrieri", nome: "Corrieri" },
  { id: "posta", nome: "Posta e messaggi" },
  { id: "conti", nome: "Fatture e numeri" },
];

export type Connessione = {
  id: string;
  nome: string;
  gruppo: GruppoConnessione;
  /** A cosa serve a OM, in una riga. */
  uso: string;
  /** Le lane che la usano: il loro ultimo giro e' lo stato della connessione. */
  lane: string[];
};

export const CONNESSIONI: Connessione[] = [
  {
    id: "amazon",
    nome: "Amazon",
    gruppo: "canali",
    uso: "Conferme di spedizione e richieste di recensione",
    lane: ["amazon_feeds", "review_request"],
  },
  {
    id: "poleepo",
    nome: "Poleepo",
    gruppo: "canali",
    uso: "Solo l'ingresso degli ordini Amazon",
    lane: ["orders"],
  },
  {
    id: "mirakl",
    nome: "Mirakl",
    gruppo: "canali",
    uso: "Leroy Merlin, Maisons du Monde, Vente-Unique",
    lane: ["mirakl", "mdm_recensione"],
  },
  { id: "metro", nome: "Metro", gruppo: "canali", uso: "Ordini e spedito", lane: ["metro"] },
  {
    id: "bricobravo",
    nome: "Brico Bravo",
    gruppo: "canali",
    uso: "Ordini e spedito",
    lane: ["bricobravo"],
  },
  {
    id: "shopify",
    nome: "Kery · Shopify",
    gruppo: "canali",
    uso: "Ordini, spedito e consegnato",
    lane: ["shopify", "kery_evasione"],
  },
  { id: "temu", nome: "Temu", gruppo: "canali", uso: "Ordini e spedito", lane: ["temu"] },

  {
    id: "allservice",
    nome: "AllService",
    gruppo: "fornitori",
    uso: "Mail d'ordine e file tracking",
    lane: ["as_invio", "as_tracking"],
  },
  {
    id: "cosma",
    nome: "Cosma",
    gruppo: "fornitori",
    uso: "Ordini caricati sul loro FTP",
    lane: ["cosma_invio"],
  },
  {
    id: "bizzotto",
    nome: "Bizzotto",
    gruppo: "fornitori",
    uso: "Ordini sul portale my.bizzotto.com",
    lane: ["bizzotto_invio"],
  },
  {
    id: "itamoby",
    nome: "Itamoby",
    gruppo: "fornitori",
    uso: "Ordini sul portale",
    lane: ["itamoby_invio"],
  },
  {
    id: "ityhome",
    nome: "Ityhome",
    gruppo: "fornitori",
    uso: "Ordini sul portale",
    lane: ["ityhome_invio"],
  },
  {
    id: "webfurniture",
    nome: "Web Furniture",
    gruppo: "fornitori",
    uso: "Mail d'ordine con la LDV",
    lane: ["wf_invio"],
  },
  {
    id: "matecpro",
    nome: "MATECPRO",
    gruppo: "fornitori",
    uso: "Giacenze del magazzino BS COM",
    lane: ["warehouse_stock"],
  },

  {
    id: "tracking",
    nome: "Poste, SDA e BRT",
    gruppo: "corrieri",
    uso: "Dove sono i pacchi",
    lane: ["tracking_events"],
  },
  {
    id: "sda_ritiri",
    nome: "SDA · ritiri",
    gruppo: "corrieri",
    uso: "Prenotazione del corriere",
    lane: ["pickups_auto", "reclami_bracchi"],
  },

  {
    id: "posta_fornitori",
    nome: "Posta fornitori",
    gruppo: "posta",
    uso: "Conferme, merce pronta, invio LDV",
    lane: ["supplier_mail", "ldv_ritiro_auto"],
  },
  {
    id: "posta_clienti",
    nome: "Messaggi clienti",
    gruppo: "posta",
    uso: "Marketplace e posta in Messaggi",
    lane: ["mail"],
  },
  {
    id: "whatsapp",
    nome: "WhatsApp",
    gruppo: "posta",
    uso: "Avvisi e messaggi dei clienti Kery",
    lane: ["whatsapp_avvisi", "whatsapp"],
  },

  {
    id: "eureka",
    nome: "Eureka",
    gruppo: "conti",
    uso: "Fatture, note di credito, registro",
    lane: ["invoicing", "fatture_auto"],
  },
  {
    id: "fatture_estere",
    nome: "Fatture estere",
    gruppo: "conti",
    uso: "Posta fatture, Mirakl, robot Metro",
    lane: ["fatture_estere"],
  },
  {
    id: "saldi_marketplace",
    nome: "Saldi marketplace",
    gruppo: "conti",
    uso: "Saldo dei conti Mirakl, Shopify Payments, PayPal",
    lane: ["saldi_marketplace"],
  },
  {
    id: "analisi_marketplace",
    nome: "Analisi Marketplace",
    gruppo: "conti",
    uso: "Commissioni e costi per il margine",
    lane: ["economics_params", "order_margins"],
  },
  {
    id: "slack",
    nome: "Slack",
    gruppo: "conti",
    uso: "Report del giorno e avviso fatture",
    lane: ["slack_report"],
  },
];

export type StatoConnessione = "errore" | "ok" | "spenta" | "mai";

export type LetturaConnessione = {
  stato: StatoConnessione;
  quando: string | null;
  motivo: string | null;
  /** La lane da cui viene lo stato: e' il link per andare a vederla. */
  lane: string | null;
};

/** Stato di una connessione dalle sue lane accese: basta un errore (o una
 *  lane fermata) per dirla in errore; altrimenti vale il contatto riuscito
 *  piu' recente. Nessuna lane accesa = nessuna prova, e lo diciamo. */
export function leggiConnessione(
  conn: Connessione,
  letture: Map<string, Lettura>,
): LetturaConnessione {
  const accese = conn.lane
    .map((m) => ({ m, l: letture.get(m) }))
    .filter((x): x is { m: string; l: Lettura } => !!x.l && x.l.stato !== "spenta");
  if (accese.length === 0) return { stato: "spenta", quando: null, motivo: null, lane: null };
  const guasta = accese.find((x) => x.l.stato === "errore" || x.l.stato === "fermata");
  if (guasta) {
    return { stato: "errore", quando: guasta.l.quando, motivo: guasta.l.motivo, lane: guasta.m };
  }
  const riuscite = accese
    .filter((x) => x.l.quando && x.l.stato !== "mai")
    .sort((a, b) => Date.parse(b.l.quando ?? "") - Date.parse(a.l.quando ?? ""));
  const ultima = riuscite[0];
  if (!ultima) return { stato: "mai", quando: null, motivo: null, lane: accese[0]?.m ?? null };
  return { stato: "ok", quando: ultima.l.quando, motivo: null, lane: ultima.m };
}

/** Gli orari fissi della giornata, per la linea del tempo. ``lane`` dice da
 *  chi dipendono: con la lane spenta il segno non si disegna. */
export type Appuntamento = { ora: number; minuti?: number; nome: string; lane: string };

export const APPUNTAMENTI: Appuntamento[] = [
  { ora: 1, nome: "Robot Metro", lane: "fatture_estere" },
  { ora: 3, nome: "Posta fornitori a 90 giorni", lane: "supplier_mail" },
  { ora: 4, nome: "Tempi di consegna", lane: "tempi_consegna" },
  { ora: 7, nome: "Robot Metro", lane: "fatture_estere" },
  { ora: 10, nome: "Report Slack", lane: "slack_report" },
  { ora: 13, nome: "Robot Metro", lane: "fatture_estere" },
  { ora: 15, nome: "Posta fornitori a 90 giorni", lane: "supplier_mail" },
  { ora: 16, nome: "Avviso fatture su Slack", lane: "fatture_auto" },
  { ora: 18, nome: "Giro fatture", lane: "fatture_auto" },
  { ora: 18, nome: "Spedito alla scadenza", lane: "spedizione_scadenza" },
  { ora: 19, nome: "Robot Metro", lane: "fatture_estere" },
  { ora: 23, nome: "Spedito alla scadenza", lane: "spedizione_scadenza" },
];

/** «oggi 15:29», «ieri 19:44», «27/10 08:12»: come si dice a voce. */
export function quandoBreve(iso: string | null, adesso: Date = new Date()): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const ora = d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
  const giorno = (x: Date) => `${x.getFullYear()}-${x.getMonth()}-${x.getDate()}`;
  if (giorno(d) === giorno(adesso)) return `oggi ${ora}`;
  const ieri = new Date(adesso);
  ieri.setDate(ieri.getDate() - 1);
  if (giorno(d) === giorno(ieri)) return `ieri ${ora}`;
  const data = d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" });
  return `${data} ${ora}`;
}

/** «ogni 15 minuti», «ogni ora», «una volta al giorno». */
export function ogniQuanto(seconds: number): string {
  if (seconds < 60) return `ogni ${seconds} secondi`;
  if (seconds === 60) return "ogni minuto";
  if (seconds < 3600) return `ogni ${Math.round(seconds / 60)} minuti`;
  if (seconds === 3600) return "ogni ora";
  if (seconds === 86400) return "una volta al giorno";
  return `ogni ${Math.round(seconds / 3600)} ore`;
}
